/**
 * OwnerPageChrome - Shared hero/backdrop and sticky header component
 * Extracted from Calendar page to provide consistent chrome across Owner Portal pages
 * 
 * Features:
 * - Fixed hero backdrop that persists behind content
 * - Scroll-activated sticky header that docks after threshold
 * - Correct z-index hierarchy to keep content above backdrop
 * - Responsive sizing (mobile/desktop)
 * - Progressive hero image loading
 * 
 * Contract:
 * - Matches Calendar page behavior exactly
 * - All constants mirror Calendar implementation
 * - Pixel-identical output when used correctly
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useSession } from '../../contexts/SessionContext.jsx';
import { useViewport } from '../../contexts/ViewportContext.jsx';

// Constants (mirror Calendar exactly)
const HERO_HEIGHT_MOBILE = 420;
const HERO_HEIGHT_DESKTOP = 800;
const HERO_HEIGHT_COMPACT_MOBILE = 120;
const HERO_HEIGHT_COMPACT_DESKTOP = 160;
const HERO_Z_INDEX = 0;
const FADE_Z_INDEX = 1;
const OVERLAY_Z_INDEX = 2;
const HEADER_Z_INDEX = 150;
const CONTENT_Z_INDEX = 20;
const CONTAINER_Z_INDEX = 1;
const HEADER_UNDOCKED_MARGIN_MOBILE = 24;
const HEADER_UNDOCKED_MARGIN_DESKTOP = 32;

// Explicit header card heights (compact vs non-compact, mobile vs desktop) so image fills without inflating row.
const HEADER_HEIGHT_NON_COMPACT_DESKTOP = 110;
const HEADER_HEIGHT_NON_COMPACT_MOBILE = 96;
const HEADER_HEIGHT_COMPACT_DESKTOP = 90;
const HEADER_HEIGHT_COMPACT_MOBILE = 80;
// When header has multi-row controls (e.g. Analytics: headerRightSlot), use taller cap so filters are never clipped.
const HEADER_HEIGHT_WITH_CONTROLS_DESKTOP = 184;
const HEADER_HEIGHT_WITH_CONTROLS_MOBILE = 160;
const HEADER_HEIGHT_COMPACT_WITH_CONTROLS_DESKTOP = 110;
const HEADER_HEIGHT_COMPACT_WITH_CONTROLS_MOBILE = 100;

// Debug flag (from env or URL param)
const CHROME_DEBUG = typeof window !== 'undefined' && (
  import.meta.env.VITE_OWNER_CHROME_DEBUG === '1' ||
  new URLSearchParams(window.location.search).get('chrome_debug') === '1'
);

export default function OwnerPageChrome({
  title,
  subtitle,
  headerRightSlot,
  headerTopRightSlot, // NEW: For Row 1 icons aligned with title
  children,
  enableDocking = true,
  onTitleClick,
  showPropertyLinks = true,
  /** When true, constrain entire chrome to 100vh so only inner content scrolls (e.g. P&T table). No body scroll. */
  contentConstrained = false,
  /** When true, use a compact hero and smaller title block (e.g. Reservations, Financials). */
  compactLayout = false,
  /** Max width of the header card (e.g. '1400px', '1600px'). Must match page content container for alignment. */
  headerMaxWidth = '1400px',
}) {
  const { selectedProperty } = useSession();
  const { isMobile } = useViewport();
  const [isDocked, setIsDocked] = useState(false);
  const [heroImageReady, setHeroImageReady] = useState(false);
  const dockSentinelRef = useRef(null);
  const heroImageRef = useRef(null);

  // Get property display name (clc_short_name > display_name > property_name/name)
  // Priority: clc_short_name (source of truth from clc_listings) > display_name (computed) > fallbacks
  const getPropertyShortName = (property) => {
    // Debug logging (gated by ?perf_debug=1)
    const perfDebug = typeof window !== 'undefined' && (
      import.meta.env.VITE_OWNER_PERF_DEBUG === '1' ||
      new URLSearchParams(window.location.search).get('perf_debug') === '1'
    );
    
    // CRITICAL: Check clc_short_name first, but only if it's non-empty string
    // Empty string should fall through to next option
    const clcShortName = property?.clc_short_name;
    const hasClcShortName = clcShortName && typeof clcShortName === 'string' && clcShortName.trim().length > 0;
    
    const result = hasClcShortName 
      ? clcShortName.trim()
      : (property?.display_name || property?.name || property?.property_name || 'Unnamed Property');
    
    if (perfDebug && property) {
      console.log('[OWNER_HEADER] Property name resolution:', {
        listing_id: property.listing_id,
        clc_short_name: property.clc_short_name || null,
        clc_short_name_type: typeof property?.clc_short_name,
        clc_short_name_length: property?.clc_short_name?.length || 0,
        hasClcShortName,
        display_name: property.display_name || null,
        name: property.name || null,
        property_name: property.property_name || null,
        chosen: result,
        source: hasClcShortName ? 'clc_short_name' : 
                property?.display_name ? 'display_name' : 
                property?.name ? 'name' : 
                property?.property_name ? 'property_name' : 'fallback',
        fullProperty: property, // Full object for inspection
      });
    }
    
    return result;
  };

  // Get property hero image for header banner (hero_url only, not thumb)
  const getPropertyHero = (property) => {
    return property?.hero_url || null;
  };

  // Get property thumbnail for avatar/picker (thumb_url only, curated-only contract)
  const getPropertyThumb = (property) => {
    return property?.thumb_url || null;
  };

  // Memoize property display values
  const propertyDisplayValues = useMemo(() => {
    if (!selectedProperty) {
      return {
        shortName: title || 'Property',
        hero: null,
        thumb: null,
      };
    }
    return {
      shortName: title || getPropertyShortName(selectedProperty),
      hero: getPropertyHero(selectedProperty),
      thumb: getPropertyThumb(selectedProperty),
    };
  }, [selectedProperty, title]);

  const propertyHero = propertyDisplayValues.hero;
  const propertyThumb = propertyDisplayValues.thumb;
  const propertyShortName = propertyDisplayValues.shortName;
  const hasProperty = !!selectedProperty;

  // Scroll-activated sticky: use IntersectionObserver to detect when header should dock
  useEffect(() => {
    if (!enableDocking || !dockSentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const shouldDock = !entry.isIntersecting;
        setIsDocked(shouldDock);
        if (CHROME_DEBUG) {
          console.log('[OWNER_CHROME] Dock state:', { isDocked: shouldDock, isIntersecting: entry.isIntersecting });
        }
      },
      {
        threshold: 0,
        rootMargin: '0px',
      }
    );

    observer.observe(dockSentinelRef.current);

    return () => {
      observer.disconnect();
    };
  }, [enableDocking]);

  // Preload hero image (non-blocking)
  useEffect(() => {
    if (!propertyHero) {
      setHeroImageReady(false);
      return;
    }

    const img = new Image();
    img.onload = () => {
      setHeroImageReady(true);
      if (CHROME_DEBUG) {
        console.log('[OWNER_CHROME] Hero image loaded:', propertyHero);
      }
    };
    img.onerror = () => {
      setHeroImageReady(false);
      if (CHROME_DEBUG) {
        console.warn('[OWNER_CHROME] Hero image failed to load:', propertyHero);
      }
    };
    img.src = propertyHero;
  }, [propertyHero]);

  // Debug: Log z-index hierarchy
  useEffect(() => {
    if (CHROME_DEBUG) {
      console.log('[OWNER_CHROME] Z-index hierarchy:', {
        heroBackdrop: HERO_Z_INDEX,
        heroFade: FADE_Z_INDEX,
        heroOverlay: OVERLAY_Z_INDEX,
        container: CONTAINER_Z_INDEX,
        content: CONTENT_Z_INDEX,
        header: HEADER_Z_INDEX,
      });
    }
  }, []);

  return (
    <div
      className="owner-dashboard-container"
      style={{
        position: 'relative',
        zIndex: CONTAINER_Z_INDEX,
        ...(contentConstrained ? { height: '100vh', maxHeight: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' } : {}),
      }}
    >
      {/* Hero Backdrop - Fixed background layer that persists behind content */}
      <div style={{
        ...styles.getHeroBackdropStyle(isMobile, compactLayout),
        backgroundImage: (propertyHero && heroImageReady)
          ? `url(${propertyHero})`
          : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #f8f9fa 100%)', // Premium neutral gradient fallback
        transition: 'background-image 0.3s ease',
      }}>
        {propertyHero && heroImageReady && <div style={styles.heroOverlay} />}
        {/* Fade overlay: transitions hero to neutral background */}
        <div style={styles.heroBackdropFade} />
      </div>

      {/* Sentinel for docking detection (invisible, placed before header) */}
      {enableDocking && <div ref={dockSentinelRef} style={styles.sentinel} />}

      {/* Header Card - Scroll-activated sticky; image fills left column height, content right */}
      <div style={styles.getHeaderCardStyle(isMobile, isDocked, enableDocking, compactLayout, headerMaxWidth)}>
        <div style={styles.getHeaderCardWrapperStyle(compactLayout, isMobile, !!headerRightSlot)}>
          {/* Left: Property image column - full card height, no padding, rounded left edge */}
          <div style={styles.getHeaderImageColumnStyle(compactLayout)}>
            {propertyThumb && hasProperty ? (
              <img
                src={propertyThumb}
                alt=""
                style={styles.headerCardImage}
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const placeholder = e.target.nextSibling;
                  if (placeholder) placeholder.style.display = 'flex';
                }}
              />
            ) : null}
            <div style={{
              ...styles.avatarPlaceholder,
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: (propertyThumb && hasProperty) ? 'none' : 'flex',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          {/* Right: Title + slots */}
          <div style={styles.getHeaderContentColumnStyle(compactLayout, !!headerRightSlot)}>
          {/* Row 1: Title + Icons */}
          <div style={styles.headerCardContent}>
            {/* Left: Title only (image is in column above) */}
            <div style={styles.headerLeft}>
            <div style={styles.headerTitleSection}>
              <h1
                style={{
                  ...styles.getHeaderTitleStyle(compactLayout),
                  ...(onTitleClick ? styles.clickableTitle : {}),
                }}
                onClick={onTitleClick}
                onKeyDown={(e) => {
                  if (onTitleClick && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onTitleClick();
                  }
                }}
                tabIndex={onTitleClick ? 0 : -1}
                role={onTitleClick ? "button" : undefined}
                aria-label={onTitleClick ? "View property overview" : undefined}
              >
                {hasProperty ? propertyShortName : (title || 'Loading property...')}
              </h1>
              {!compactLayout && (
              <p style={styles.headerSubtitle}>
                {subtitle || (hasProperty ? 'Property details' : 'Loading...')}
                {/* Build stamp moved to OwnerLayout footer to avoid "Error [b:…]" confusion */}
                {/* TEMPORARY DEBUG INFO - gated by ?perf_debug=1 */}
                {typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perf_debug') === '1' && selectedProperty && (
                  <span style={{ 
                    fontSize: '10px', 
                    color: '#f59e0b', 
                    marginLeft: '8px',
                    fontFamily: 'monospace',
                    display: 'block',
                    marginTop: '4px'
                  }}>
                    clc:{selectedProperty.clc_short_name || 'NULL'} | 
                    disp:{selectedProperty.display_name || 'NULL'} | 
                    name:{selectedProperty.name || 'NULL'} | 
                    chosen:{propertyShortName}
                  </span>
                )}
              </p>
              )}
            </div>
          </div>

            {/* Right: Row 1 - Icons aligned with title (same row) */}
            {headerTopRightSlot && (
              <div style={styles.headerTopRight}>
                {headerTopRightSlot}
              </div>
            )}
          </div>

          {/* Row 2: Controls (inside header card, below title row) */}
          {headerRightSlot && (
            <div style={styles.headerControlsRow}>
              {headerRightSlot}
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Page Content - Must have z-index 20 to stay above backdrop. When contentConstrained, flex so only children scroll. */}
      <div
        style={{
          position: 'relative',
          zIndex: CONTENT_Z_INDEX,
          ...(contentConstrained ? { flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' } : {}),
        }}
      >
        {children}
      </div>
    </div>
  );
}

const styles = {
  // Hero Backdrop - Fixed background layer that persists behind content
  getHeroBackdropStyle: (isMobile, compactLayout) => ({
    position: 'fixed', // Fixed positioning relative to viewport (persists on scroll)
    top: 'var(--owner-top-nav-height, 52px)', // Start below global nav
    left: 0,
    right: 0,
    height: compactLayout
      ? (isMobile ? `${HERO_HEIGHT_COMPACT_MOBILE}px` : `${HERO_HEIGHT_COMPACT_DESKTOP}px`)
      : (isMobile ? `${HERO_HEIGHT_MOBILE}px` : `${HERO_HEIGHT_DESKTOP}px`),
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    zIndex: HERO_Z_INDEX, // Behind all content
    backgroundColor: '#f8f9fa', // Fallback color if gradient doesn't load
    pointerEvents: 'none', // Don't block interactions
  }),
  heroBackdropFade: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // Vertical fade overlay: transitions from transparent at top to neutral at bottom
    background: 'linear-gradient(180deg, rgba(248, 249, 250, 0) 0%, rgba(248, 249, 250, 0.2) 50%, rgba(248, 249, 250, 0.7) 80%, rgba(248, 249, 250, 1) 100%)',
    zIndex: FADE_Z_INDEX,
    pointerEvents: 'none', // Don't block interactions
  },
  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0.25) 50%, rgba(0, 0, 0, 0.0) 100%)',
    // Overlay only shown when hero image is loaded (for text readability)
    zIndex: OVERLAY_Z_INDEX, // Above backdrop, below fade
  },
  // Sentinel for docking detection (invisible)
  sentinel: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '1px',
    height: '1px',
    pointerEvents: 'none',
    visibility: 'hidden',
  },
  // Header Card - Scroll-activated sticky property navigation bar with glass styling.
  // Width: headerMaxWidth so each page can match its content container (e.g. Calendar 1400, Financials 1600).
  getHeaderCardStyle: (isMobile, isDocked, enableDocking, compactLayout, headerMaxWidth) => {
    const baseStyle = {
      zIndex: HEADER_Z_INDEX, // Below global nav (200/sticky) but above content
      marginBottom: compactLayout ? 'var(--spacing-2)' : 'var(--spacing-4)',
      maxWidth: headerMaxWidth ?? '1400px',
      marginLeft: 'auto',
      marginRight: 'auto',
      padding: 0, // No horizontal padding so card width matches content container below
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    };

    if (enableDocking && isDocked) {
      return {
        ...baseStyle,
        position: 'sticky',
        top: 'calc(var(--owner-top-nav-height, 52px) + var(--owner-sticky-gap, 12px))',
        marginTop: 0,
      };
    } else {
      const marginTop = compactLayout ? (isMobile ? 12 : 16) : (isMobile ? HEADER_UNDOCKED_MARGIN_MOBILE : HEADER_UNDOCKED_MARGIN_DESKTOP);
      return {
        ...baseStyle,
        position: 'relative',
        marginTop: `${marginTop}px`,
      };
    }
  },
  getHeaderCardWrapperStyle: (compactLayout, isMobile, hasControlsRow) => {
    const height = hasControlsRow
      ? (compactLayout
          ? (isMobile ? HEADER_HEIGHT_COMPACT_WITH_CONTROLS_MOBILE : HEADER_HEIGHT_COMPACT_WITH_CONTROLS_DESKTOP)
          : (isMobile ? HEADER_HEIGHT_WITH_CONTROLS_MOBILE : HEADER_HEIGHT_WITH_CONTROLS_DESKTOP))
      : (compactLayout
          ? (isMobile ? HEADER_HEIGHT_COMPACT_MOBILE : HEADER_HEIGHT_COMPACT_DESKTOP)
          : (isMobile ? HEADER_HEIGHT_NON_COMPACT_MOBILE : HEADER_HEIGHT_NON_COMPACT_DESKTOP));
    return {
      backgroundColor: 'rgba(255, 255, 255, 0.75)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderRadius: 'var(--radius-lg, 0.5rem)',
      borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
      display: 'flex',
      flexDirection: 'row',
      overflow: 'hidden',
      height,
      minHeight: height,
      maxHeight: height,
    };
  },
  // Image column: full card height, no padding; left edge rounded to match card; clips to wrapper height
  getHeaderImageColumnStyle: (compactLayout) => ({
    width: compactLayout ? 80 : 120,
    minWidth: compactLayout ? 80 : 120,
    flexShrink: 0,
    alignSelf: 'stretch',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    borderTopLeftRadius: 'var(--radius-lg, 0.5rem)',
    borderBottomLeftRadius: 'var(--radius-lg, 0.5rem)',
    backgroundColor: 'var(--color-bg-secondary)',
  }),
  headerCardImage: {
    width: '100%',
    height: '100%',
    minHeight: '100%',
    objectFit: 'cover',
    display: 'block',
    verticalAlign: 'middle',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  // Content column: title + slots; padding only here so image touches card edges; fits within capped height
  getHeaderContentColumnStyle: (compactLayout, hasControlsRow) => {
    const padding = compactLayout
      ? 'var(--spacing-2) var(--spacing-3)'
      : hasControlsRow
        ? 'var(--spacing-3) var(--spacing-4) var(--spacing-4) var(--spacing-4)' // extra bottom when controls row
        : 'var(--spacing-3) var(--spacing-4)';
    return {
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      gap: compactLayout ? 'var(--spacing-2)' : 'var(--spacing-3)',
      padding,
    };
  },
  headerCardWrapper: {
    // Legacy shape for any direct refs; prefer getHeaderCardWrapperStyle
    padding: 'var(--spacing-3) var(--spacing-4)',
  },
  headerCardContent: {
    // Row 1: Title + Icons (flex row)
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 'var(--spacing-3)', // 24px gap
    flexWrap: 'wrap',
    width: '100%',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--spacing-4)',
    flex: '1 1 auto',
    minWidth: 0,
  },
  getAvatarContainerStyle: (compactLayout) => ({
    width: compactLayout ? '40px' : '56px',
    height: compactLayout ? '40px' : '56px',
    borderRadius: 'var(--radius-md, 0.375rem)',
    overflow: 'hidden',
    backgroundColor: 'var(--color-bg-secondary)',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid var(--color-border-light, #e5e7eb)',
  }),
  avatarImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    flexShrink: 0,
  },
  avatarPlaceholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.4,
    display: 'flex',
    color: 'var(--color-text-secondary)',
  },
  headerTitleSection: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  getHeaderTitleStyle: (compactLayout) => ({
    fontSize: compactLayout ? 'var(--font-size-base, 1rem)' : 'var(--font-size-2xl, 1.5rem)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-text-primary, #111827)',
    margin: 0,
    marginBottom: compactLayout ? 0 : 'var(--spacing-1)',
  }),
  headerTitle: {
    fontSize: 'var(--font-size-2xl, 1.5rem)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-text-primary, #111827)',
    margin: 0,
    marginBottom: 'var(--spacing-1)',
  },
  clickableTitle: {
    cursor: 'pointer',
    textDecoration: 'underline',
    textDecorationColor: 'transparent',
    transition: 'text-decoration-color 0.2s ease',
  },
  headerSubtitle: {
    fontSize: 'var(--font-size-sm, 0.875rem)',
    color: 'var(--color-text-secondary, #6b7280)',
    margin: 0,
  },
  headerTopRight: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0,
    gap: 'var(--spacing-3)',
    minWidth: 0,
  },
  headerControlsRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 'var(--spacing-3)',
    width: '100%',
    minWidth: 0,
  },
  headerRight: {
    display: 'flex',
    flexDirection: 'column', // Stack AnalyticsHeader Row 1 (icons) and Row 2 (controls)
    alignItems: 'flex-start',
    gap: '10px', // Gap between Row 1 (icons) and Row 2 (controls)
    flexShrink: 0,
  },
};
